/**
 * 入札 CSV の固定ヘッダー (backend パーサの期待列と 1:1)。
 * DropZone のヘッダー完全一致検証と、 画面の列ガイド表示に使う。
 *
 * 通常入札・ブロック入札とも eBalance 固有の日本語列名 + 1 行ヘッダー
 * (issue #5705 / 2026-08 PdM 判断。 spec spot-bid-normal-csv-import.md §2 /
 * spot-bid-block-csv-import.md §1 §2)。 旧 eSLJ 互換 (英語列名・2 行ヘッダー) は撤回済み。
 * ブロック入札は英語表示用の英語ヘッダーも取込で受け付ける (AC-DSB-013)。
 *
 * 通常入札・ブロック入札のヘッダーは取込 (SpotCsvImport の DropZone 検証) と
 * 出力 (SpotBidding の入札一覧の CSV ダウンロード
 * [[buildNormalBidCsv]]・[[buildBlockBidCsvFiles]]) の 2 page が読むため `~/models` に置く
 * (= 複数 page で使われてから共有 directory へ昇格する、 apps/ebalance/CLAUDE.md)。
 * 列名を 1 箇所に閉じることで、 出力した CSV の取込の列がそのまま取込にかけられる状態を
 * 型と定数で保つ。
 */

/**
 * 通常入札 CSV の 11 列 (docs/specs/bidding/spot-bid-normal-csv-import.md §2 / AC-SPOT-001)。
 * 発注主体区分は行ごとの値 (画面で選択中の発注主体とは照合しない)。
 */
export const SPOT_BID_NORMAL_CSV_HEADER = [
  '受渡日',
  'コマ',
  '発注主体区分',
  'エリア',
  '売買',
  '価格種別',
  '価格 (円/kWh)',
  '数量 (MW)',
  '排出係数紐付け',
  'JEPX自由記述欄',
  'メモ',
]

/**
 * 時間前入札 CSV の 15 列 (docs/specs/bidding/intraday-csv-import.md §2)。
 *
 * 列の並びはスポット通常入札 CSV にそろえ、 `発注主体区分` (行ごとの発注主体)、
 * `基準価格`・`基準価格エリア`・`演算子`・`調整値` (フォーミュラ) と `自動取消時刻` を持つ。 列マッピング
 * UI を持たない固定ヘッダー厳格モード (時間前には対応する eSLJ 入稿仕様の運用実態が
 * 無いため新設した構成。 同 spec Discussion)。
 */
export const INTRADAY_ORDER_CSV_HEADER = [
  '受渡日',
  'コマ',
  '発注主体区分',
  'エリア',
  '売買',
  '価格種別',
  '価格 (円/kWh)',
  '基準価格',
  '基準価格エリア',
  '演算子',
  '調整値',
  '数量 (MW)',
  '自動取消時刻',
  'JEPX自由記述欄',
  'メモ',
]

/**
 * 時間前入札 CSV の英語ヘッダー (AC-BCI-001)。 日本語ヘッダーと同じ列順で、
 * どちらと完全一致しても受理する (backend `IntradayOrderCsvHeaderEnglish` と 1:1。
 * 英語の比較は大文字小文字を区別しない)。
 */
export const INTRADAY_ORDER_CSV_HEADER_EN = [
  'DeliveryDate',
  'Slot',
  'IssuerType',
  'Area',
  'BuySell',
  'OrderType',
  'OrderPrice',
  'ReferencePrice',
  'ReferenceArea',
  'FormulaOperator',
  'FormulaValue',
  'Quantity',
  'AutoCancelTime',
  'JEPXFreeText',
  'Memo',
]

/**
 * 通常入札 CSV の英語ヘッダー (AC-SPOT-001)。日本語ヘッダーと同じ列順で、
 * どちらと完全一致しても受理する (`conventions.md`「CSV の英語表示」
 * 「アップロードは、表示言語に関わらずヘッダー行・区分値とも日本語・英語の
 * どちらでも受理する」)。
 */
export const SPOT_BID_NORMAL_CSV_HEADER_EN = [
  'DeliveryDate',
  'Slot',
  'IssuerType',
  'Area',
  'BuySell',
  'OrderType',
  'OrderPrice',
  'Quantity',
  'EmissionFactorLinking',
  'JEPXFreeText',
  'Memo',
]

/** コマ列 (コマ1〜コマ48) のヘッダー。ブロック入札 CSV の後半 48 列。 */
const BLOCK_BID_KOMA_HEADER = Array.from(
  { length: 48 },
  (_, i) => `コマ${String(i + 1)}`,
)

/**
 * 通常ブロック (STANDARD) の 56 列 (固定 8 列 + コマ 48 列、
 * spec spot-bid-block-csv-import.md §1 AC-DSB-013)。
 */
export const BLOCK_BID_STANDARD_CSV_HEADER = [
  '受渡日',
  '発注主体区分',
  'エリア',
  '売買',
  '価格 (円/kWh)',
  '排出係数紐付け',
  'JEPX自由記述欄',
  'メモ',
  ...BLOCK_BID_KOMA_HEADER,
]

/**
 * リンクブロック・ループ (LINK / LOOP) の 58 列 (受渡日の後ろに ペア番号, ブロック種別
 * を挟んだ固定 10 列 + コマ 48 列、 spec spot-bid-block-csv-import.md §1 AC-DSB-013)。
 */
export const BLOCK_BID_PAIRED_CSV_HEADER = [
  '受渡日',
  'ペア番号',
  'ブロック種別',
  '発注主体区分',
  'エリア',
  '売買',
  '価格 (円/kWh)',
  '排出係数紐付け',
  'JEPX自由記述欄',
  'メモ',
  ...BLOCK_BID_KOMA_HEADER,
]

/** 英語ヘッダーのコマ列 (Slot1〜Slot48)。 */
const BLOCK_BID_KOMA_HEADER_EN = Array.from(
  { length: 48 },
  (_, i) => `Slot${String(i + 1)}`,
)

/**
 * 通常ブロックの英語ヘッダー 56 列 (spec spot-bid-block-csv-import.md AC-DSB-013・
 * §2「英語表示対応」)。 取込は日本語・英語どちらのヘッダーも受け付ける。
 */
export const BLOCK_BID_STANDARD_CSV_HEADER_EN = [
  'DeliveryDate',
  'IssuerType',
  'Area',
  'BuySell',
  'OrderPrice',
  'EmissionFactorLinking',
  'JEPXFreeText',
  'Memo',
  ...BLOCK_BID_KOMA_HEADER_EN,
]

/** リンクブロック・ループの英語ヘッダー 58 列 (同上)。 */
export const BLOCK_BID_PAIRED_CSV_HEADER_EN = [
  'DeliveryDate',
  'PairNumber',
  'BlockType',
  'IssuerType',
  'Area',
  'BuySell',
  'OrderPrice',
  'EmissionFactorLinking',
  'JEPXFreeText',
  'Memo',
  ...BLOCK_BID_KOMA_HEADER_EN,
]

/** CSV の表記の言語 (spec §2「英語表示対応」: 表示言語が英語なら英語、 それ以外は日本語)。 */
export type BidCsvLang = 'ja' | 'en'

export const bidCsvLangOf = (isEnglish: boolean): BidCsvLang =>
  isEnglish ? 'en' : 'ja'

/** 通常入札 CSV のヘッダー (表示言語ごと)。 雛形は表示言語のヘッダーで出す (spec §2「英語表示対応」)。 */
export const SPOT_BID_NORMAL_CSV_HEADER_BY_LANG: Record<BidCsvLang, string[]> =
  {
    ja: SPOT_BID_NORMAL_CSV_HEADER,
    en: SPOT_BID_NORMAL_CSV_HEADER_EN,
  }

/**
 * 時間前入札 CSV のヘッダー (表示言語ごと)。 取込の受理ヘッダー・期待ヘッダーの表示と雛形は
 * この表から引き、 DL した雛形が自画面のヘッダーチェックを通ることを保つ (spec §2「英語表示対応」)。
 */
export const INTRADAY_ORDER_CSV_HEADER_BY_LANG: Record<BidCsvLang, string[]> = {
  ja: INTRADAY_ORDER_CSV_HEADER,
  en: INTRADAY_ORDER_CSV_HEADER_EN,
}

/** ブロック入札 CSV の列構成。 通常ブロックは 56 列、 リンクブロック・ループは 58 列。 */
export type BlockBidCsvLayout = 'standard' | 'paired'

/**
 * ブロック入札 CSV のヘッダーを (言語, 列構成) で引く表。 取込のヘッダー検証 (DropZone)・
 * 雛形・入札一覧の CSV ダウンロードはすべてこの表から引く。 出力する側と
 * 検証する側が同じ表を見ることで、 出力した CSV のヘッダーが取込の受け付けるヘッダーから
 * ずれない。
 */
export const BLOCK_BID_CSV_HEADER: Record<
  BidCsvLang,
  Record<BlockBidCsvLayout, string[]>
> = {
  ja: {
    standard: BLOCK_BID_STANDARD_CSV_HEADER,
    paired: BLOCK_BID_PAIRED_CSV_HEADER,
  },
  en: {
    standard: BLOCK_BID_STANDARD_CSV_HEADER_EN,
    paired: BLOCK_BID_PAIRED_CSV_HEADER_EN,
  },
}

/** `BLOCK_BID_CSV_HEADER` が持つ列構成の一覧 (取込が受け付けるヘッダーを表から作るため)。 */
export const BLOCK_BID_CSV_LAYOUTS = Object.keys(
  BLOCK_BID_CSV_HEADER.ja,
) as BlockBidCsvLayout[]

/** 表示言語で出し分ける CSV の値 (日本語の値と英語の値の組)。 */
export type BidCsvLocalizedValue = Record<BidCsvLang, string>

/**
 * 入札一覧の CSV ダウンロード (spec `bidding/spot-bidding.md` §2 機能詳細 9) が、 取込と同じ列の
 * 後ろに足すステータス列の見出し。 取込のヘッダー検証には含まない列で、 削除すればそのまま
 * 取込にかけ直せる。 英語ヘッダー名は spec が `Status` と定める。
 */
export const BID_CSV_STATUS_HEADER: BidCsvLocalizedValue = {
  ja: 'ステータス',
  en: 'Status',
}

/**
 * ブロック入札 CSV の `発注主体区分` 列の値 (spec spot-bid-block-csv-import.md §2 列定義表)。
 * 取込が受け付ける CSV の値そのもので、 画面の表示ラベル (i18n の「需要 BG」等) とは
 * 別に持つ。 ヘッダーと同じく、 雛形の記入例 (SpotCsvImport) と入札一覧の CSV ダウンロード
 * (SpotBidding) の 2 page が同じ値を出すためここに置く。
 */
export const BLOCK_BID_CSV_ISSUER_TYPE_VALUE = {
  retail: { ja: '需要BG', en: 'Demand BG' },
  generation: { ja: '発電', en: 'Generation' },
  selfWheeling: { ja: '自己託送BG', en: 'Self-wheeling BG' },
} satisfies Record<string, BidCsvLocalizedValue>

/**
 * 入札 CSV (スポット通常・ブロック・時間前) の `エリア` 列の値 (`conventions.md`「エリア一覧と
 * 表示順序」の日本語・英語表示。 JEPX 取引対象の 9 エリア)。 雛形の記入例は表示言語の値を
 * ここから引く (取込は backend `parseSpotBidCsvArea` が両方を受理する)。
 */
export const BID_CSV_AREA_VALUE = {
  hokkaido: { ja: '北海道', en: 'Hokkaido' },
  tohoku: { ja: '東北', en: 'Tohoku' },
  tokyo: { ja: '東京', en: 'Tokyo' },
  chubu: { ja: '中部', en: 'Chubu' },
  hokuriku: { ja: '北陸', en: 'Hokuriku' },
  kansai: { ja: '関西', en: 'Kansai' },
  chugoku: { ja: '中国', en: 'Chugoku' },
  shikoku: { ja: '四国', en: 'Shikoku' },
  kyushu: { ja: '九州', en: 'Kyushu' },
} satisfies Record<string, BidCsvLocalizedValue>

/** ブロック入札 CSV の `売買` 列の値 (同上)。 */
export const BLOCK_BID_CSV_SIDE_VALUE = {
  buy: { ja: '買', en: 'Buy' },
  sell: { ja: '売', en: 'Sell' },
} satisfies Record<string, BidCsvLocalizedValue>

/**
 * スポットブロック入札 CSV の種別 (雛形・ダウンロードのどちらも、 種別ごとに別ファイルにする)。
 * 並びは雛形・記入ガイド・ダウンロードのファイルを並べる順 (通常ブロック → リンクブロック → ループ。
 * spec §3 / §6)。 列構成は `通常ブロック` が 56 列、 `リンクブロック` と `ループ` が 58 列
 * (`BlockBidCsvLayout`)。
 *
 * 取込画面の `?blockType=`・雛形・入札一覧の CSV 出力が同じ値域を見るよう、 値域はここ 1 箇所に
 * 置いて型で縛る。
 */
export const BLOCK_CSV_TYPES = ['standard', 'link', 'loop'] as const

export type BlockBidCsvKind = (typeof BLOCK_CSV_TYPES)[number]

/** 種別 → CSV の列構成 (通常ブロックは 56 列、 リンクブロック・ループは 58 列。 spec §2)。 */
export const blockCsvLayoutOf = (kind: BlockBidCsvKind): BlockBidCsvLayout =>
  kind === 'standard' ? 'standard' : 'paired'

/**
 * スポットブロック入札 CSV のファイル名のデータ種別名 (`admin/data/uploads/common.md` §6
 * 「ダウンロードファイルの命名規則」の、 雛形ごとの名前)。 雛形は後ろに `_テンプレート` /
 * `_template`、 入札一覧の CSV ダウンロードは `_YYYYMMDD` を付ける。 列数は入れない。
 * 2 page (SpotCsvImport の雛形、 SpotBidding の CSV 出力) が同じ名前を使うためここに置く。
 */
export const BLOCK_BID_CSV_DATA_TYPE_NAME: Record<
  BlockBidCsvKind,
  BidCsvLocalizedValue
> = {
  standard: {
    ja: 'スポットブロック入札 (通常ブロック)',
    en: 'spot-bid-block-standard',
  },
  link: {
    ja: 'スポットブロック入札 (リンクブロック)',
    en: 'spot-bid-block-link',
  },
  loop: { ja: 'スポットブロック入札 (ループ)', en: 'spot-bid-block-loop' },
}

/**
 * スポット通常入札 CSV のファイル名のデータ種別名 (`admin/data/uploads/common.md` §6
 * 「ダウンロードファイルの命名規則」。 日本語表示は individual spec のタイトル、 英語表示は
 * 英語データ種別名 `spot-bid-normal`)。 雛形は後ろに `_テンプレート` / `_template`、 入札一覧の
 * CSV ダウンロードは `_YYYYMMDD` を付ける。 2 page (SpotCsvImport の雛形、
 * SpotBidding の CSV 出力) が同じ名前を使うためここに置く。
 */
export const SPOT_BID_NORMAL_CSV_DATA_TYPE_NAME: BidCsvLocalizedValue = {
  ja: 'スポット通常入札',
  en: 'spot-bid-normal',
}

/**
 * 通常入札 CSV の `価格種別` 列の値 (spec spot-bid-normal-csv-import.md §2 列定義表)。
 * 発注主体区分・売買は通常入札 CSV もブロック入札 CSV と同じ値なので、
 * `BLOCK_BID_CSV_ISSUER_TYPE_VALUE` / `BLOCK_BID_CSV_SIDE_VALUE` を使う。
 */
export const SPOT_BID_CSV_ORDER_TYPE_VALUE = {
  limit: { ja: '指値', en: 'Limit' },
  market: { ja: '成行', en: 'Market' },
} satisfies Record<string, BidCsvLocalizedValue>
